import { type ReactNode, useState } from 'react';
import { Box, Button, Snackbar, Typography } from '@mui/material';
import { copyEmailsLabel } from '../benchmarkMonitoring';
import { BENCH } from '../tokens';

export const OUTLINED_SX = {
  fontFamily: BENCH.font,
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 700,
  textTransform: 'none',
  color: BENCH.action,
  bgcolor: BENCH.cardBg,
  border: `1px solid ${BENCH.action}`,
  borderRadius: '8px',
  p: '8px 16px',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'rgba(8, 8, 27, 0.04)', border: `1px solid ${BENCH.action}` },
} as const;

export function PageHeader({ title, subtitle, caption, action }: { title: string; subtitle: string; caption?: string; action?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '24px' }}>
        <Box>
          <Typography component="h1" sx={{ fontFamily: BENCH.font, fontSize: '32px', lineHeight: '40px', fontWeight: 800, letterSpacing: '-0.02em', color: BENCH.ink, m: 0 }}>
            {title}
          </Typography>
          <Typography component="p" sx={{ fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: BENCH.muted, m: '4px 0 0' }}>
            {subtitle}
          </Typography>
          {caption && (
            <Typography component="p" data-testid="page-refreshed" sx={{ fontFamily: BENCH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 500, color: BENCH.muted, m: '4px 0 0' }}>
              {caption}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
    </Box>
  );
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function CopyEmailsButton({ emails, open }: { emails: string[]; open: boolean }) {
  const [message, setMessage] = useState<string | null>(null);
  if (emails.length === 0) return null;
  const count = emails.length;
  return (
    <>
      <Button
        variant="outlined"
        disableRipple
        sx={OUTLINED_SX}
        onClick={() => {
          void writeClipboard(emails.join(', ')).then((ok) =>
            setMessage(ok ? `Copied ${count} teacher email ${count === 1 ? 'address' : 'addresses'}` : 'Could not copy the emails'),
          );
        }}
      >
        {copyEmailsLabel(count, open)}
      </Button>
      <Snackbar open={message !== null} autoHideDuration={2400} onClose={() => setMessage(null)} message={message ?? ''} />
    </>
  );
}

export function EmailTeacherButton({ firstName, email }: { firstName: string; email: string }) {
  if (!email) return null;
  return (
    <Button component="a" href={`mailto:${email}`} variant="outlined" disableRipple sx={OUTLINED_SX}>
      {`Email ${firstName}`}
    </Button>
  );
}
